import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StateSelect from "@/components/StateSelect";
import OwnerDashboardOverview from "@/components/OwnerDashboardOverview";
import OwnerSidebarNav from "@/components/OwnerSidebarNav";
import OwnerDashboardRightRail from "@/components/OwnerDashboardRightRail";
import OwnerEmptyState from "@/components/OwnerEmptyState";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Store, Package, Plus, Edit, Trash2, DollarSign, ShoppingBag, 
  TrendingUp, Star, Eye, BadgeCheck, BarChart3, Users, QrCode, Sparkles, Heart, MapPin, GraduationCap,
  CreditCard, MessageSquare, Share2, Settings as SettingsIcon,
} from "lucide-react";
import ImageUpload from "@/components/ImageUpload";
import MultiImageUpload from "@/components/MultiImageUpload";
import ProductModifierEditor from "@/components/dashboard/ProductModifierEditor";
import FulfillmentSettingsCard from "@/components/dashboard/FulfillmentSettingsCard";
import SocialLinksInput from "@/components/SocialLinksInput";
import { resolveAssetUrl } from "@/lib/assetUrl";
import Navbar from "@/components/Navbar";
import AdminHeaderBusinessSearch from "@/components/AdminHeaderBusinessSearch";
import BusinessCardScanDialog from "@/components/BusinessCardScanDialog";
import Footer from "@/components/Footer";
import SubscriptionCard from "@/components/SubscriptionCard";
import StripeConnectCard from "@/components/StripeConnectCard";import AnalyticsCard from "@/components/AnalyticsCard";
import FeatureSlotCard from "@/components/FeatureSlotCard";
import DigestOptInToggle from "@/components/DigestOptInToggle";
import BulkProductUpload from "@/components/BulkProductUpload";
import BannerAdsCard from "@/components/BannerAdsCard";
import ReferralCard from "@/components/ReferralCard";
import BusinessLogo from "@/components/BusinessLogo";
import BlinterestBusinessSection from "@/components/BlinterestBusinessSection";
import BlinterestAnalyticsCard from "@/components/BlinterestAnalyticsCard";
import OwnerNotificationsBanner from "@/components/OwnerNotificationsBanner";
import GetStartedChecklist from "@/components/academy/GetStartedChecklist";
import DashboardTour from "@/components/academy/DashboardTour";
import FirstTimeWelcome from "@/components/academy/FirstTimeWelcome";
import ContextualHelp from "@/components/academy/ContextualHelp";
import RealtorOwnerEditor from "@/components/RealtorOwnerEditor";

export default function DashboardPage() {
  const { user, loading: authLoading, login } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [business, setBusiness] = useState(null);
  const [businesses, setBusinesses] = useState([]); // all businesses owned by this user
  const [adminStats, setAdminStats] = useState(null); // super-admin only: platform-wide totals
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [businessDialogOpen, setBusinessDialogOpen] = useState(false);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "overview");
  const [favorites, setFavorites] = useState([]);
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  // Academy — tour running state. Triggered by first-time welcome, the
  // "Start Tour" button on the checklist, or a ?startTour=1 query param.
  const [tourRunning, setTourRunning] = useState(false);

  // Sync deep-link ?tab= changes (e.g., from notification dropdown)
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t) setActiveTab(t);
    // setActiveTab is stable; only searchParams should trigger this.
  }, [searchParams]);

  // Super-admin: fetch platform-wide totals once so the switcher and stats
  // cards show the true numbers ("2,785 businesses", founding_members, etc.)
  // instead of the count of the admin's own owned listings.
  useEffect(() => {
    if (!user?.is_admin) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/stats`, { withCredentials: true });
        if (!cancelled) setAdminStats(r.data);
      } catch (e) {
        // Non-fatal — falls back to owner-scoped count.
        if (!cancelled) setAdminStats(null);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.is_admin]);

  // Trigger the guided tour when ?startTour=1 is present (deep-linked
  // from the Business Academy "Restart tour" button).
  useEffect(() => {
    if (searchParams.get("startTour") === "1") {
      // Delay a tick so mount-critical elements settle before Joyride reads
      // the DOM to compute the first spotlight position.
      const id = setTimeout(() => setTourRunning(true), 400);
      return () => clearTimeout(id);
    }
  }, [searchParams]);

  // Business form state
  const [businessForm, setBusinessForm] = useState({
    name: "",
    description: "",
    category: "",
    custom_category: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    phone: "",
    email: "",
    website: "",
    image_url: "",
    social_links: {}
  });
  // Business-card scanner (photo → auto-fill the form above)
  const [scanOpen, setScanOpen] = useState(false);
  const applyScannedCard = (data) => {
    // Non-destructive merge — never blow away a field the user has already typed.
    setBusinessForm((prev) => ({
      ...prev,
      name: prev.name || data.name || "",
      description: prev.description || data.description || "",
      address: prev.address || data.address || "",
      city: prev.city || data.city || "",
      state: prev.state || data.state || "",
      zip_code: prev.zip_code || data.zip_code || "",
      phone: prev.phone || data.phone || "",
      email: prev.email || data.email || "",
      website: prev.website || data.website || "",
      social_links: {
        ...(prev.social_links || {}),
        ...Object.fromEntries(
          Object.entries(data.social_links || {}).filter(([, v]) => v && v.trim()),
        ),
      },
    }));
  };

  // Product form state
  const [productForm, setProductForm] = useState({
    name: "",
    description: "",
    price: "",
    category: "",
    image_url: "",
    gallery_images: [],
    inventory: 0,
    modifier_groups: [],
    add_ons: [],
  });

  useEffect(() => {
    if (!authLoading && !user) {
      login();
    } else if (user) {
      fetchData();
    }
  }, [user, authLoading]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Super-Admin impersonation deep-link: ?admin_business_id=biz_xxx
      // tells the dashboard to operate on a business the current user
      // does NOT own. The backend enforces admin permission — if the
      // caller isn't an admin the fetch fails and we fall back to the
      // regular /my-businesses flow. Kept intentionally out of history
      // so a bookmarked/shared dashboard URL doesn't leak into the
      // next session (see the effect that strips the param below).
      const adminBusinessId = searchParams.get("admin_business_id");

      const [catRes, bizRes, adminBizRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/my-businesses`, { withCredentials: true }),
        adminBusinessId
          ? axios.get(`${API}/admin/businesses/search`, {
              params: { q: adminBusinessId, limit: 1 },
              withCredentials: true,
            }).catch(() => null)
          : Promise.resolve(null),
      ]);

      setCategories(catRes.data.categories || []);

      const ownedBiz = bizRes.data.businesses || [];
      // If we're in admin-impersonation mode and successfully fetched the
      // target business, prepend it to the picker so the "Currently
      // editing" state has something to bind to. We tag it so the UI can
      // paint the Super Admin banner.
      let impersonated = null;
      if (adminBizRes?.data?.businesses?.length === 1) {
        impersonated = { ...adminBizRes.data.businesses[0], _admin_impersonated: true };
      }
      const allBiz = impersonated
        ? [impersonated, ...ownedBiz.filter((b) => b.business_id !== impersonated.business_id)]
        : ownedBiz;
      setBusinesses(allBiz);

      // Preserve the currently selected business across refreshes if it's
      // still in the owner's list. Otherwise prefer the impersonated one,
      // then default to the first owned business.
      const previouslySelectedId = business?.business_id;
      const selected =
        (impersonated && (!previouslySelectedId || previouslySelectedId === impersonated.business_id)
          ? impersonated
          : null) ||
        allBiz.find((b) => b.business_id === previouslySelectedId) ||
        allBiz[0] ||
        null;

      if (selected) {
        setBusiness(selected);
        setBusinessForm({
          ...selected,
          custom_category: selected.custom_category || ""
        });

        // Fetch products and orders for the selected business
        const [prodRes, ordersRes] = await Promise.all([
          axios.get(`${API}/businesses/${selected.business_id}/products?active_only=false`),
          axios.get(`${API}/business-orders`, { withCredentials: true })
        ]);
        setProducts(prodRes.data.products || []);
        setOrders(ordersRes.data.orders || []);
      } else {
        setBusiness(null);
      }
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Switch the dashboard to a different owned business. Re-fetches its products
  // so the orders/products/analytics views update.
  const switchBusiness = async (newBusiness) => {
    if (!newBusiness || newBusiness.business_id === business?.business_id) return;
    setBusiness(newBusiness);
    setBusinessForm({
      ...newBusiness,
      custom_category: newBusiness.custom_category || ""
    });
    setProducts([]); // clear so the user doesn't briefly see the old business's products
    try {
      const prodRes = await axios.get(
        `${API}/businesses/${newBusiness.business_id}/products?active_only=false`
      );
      setProducts(prodRes.data.products || []);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error("Error switching business:", error);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const res = await axios.patch(
        `${API}/business-orders/${orderId}/status`,
        { status: newStatus },
        { withCredentials: true }
      );
      setOrders((prev) =>
        prev.map((o) => (o.order_id === orderId ? { ...o, ...(res.data?.order || {}), status: newStatus } : o))
      );
      toast.success(`Order marked as ${newStatus}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update order");
    }
  };

  const handleBusinessSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const submitData = { ...businessForm };
      if (businessForm.category !== "Other") {
        submitData.custom_category = "";
      }
      
      if (business) {
        await axios.put(
          `${API}/businesses/${business.business_id}`,
          submitData,
          { withCredentials: true }
        );
        toast.success("Business updated!");
      } else {
        const response = await axios.post(
          `${API}/businesses`,
          submitData,
          { withCredentials: true }
        );
        setBusiness(response.data);
        toast.success("Business created!");
      }
      setBusinessDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save business");
    }
  };

  const fetchFavorites = async () => {
    setFavoritesLoading(true);
    try {
      const res = await axios.get(`${API}/favorites`, { withCredentials: true });
      setFavorites(res.data.businesses || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't load favorites");
    } finally {
      setFavoritesLoading(false);
    }
  };

  const removeFavorite = async (businessId) => {
    try {
      await axios.delete(`${API}/favorites/${businessId}`, { withCredentials: true });
      setFavorites((prev) => prev.filter((b) => b.business_id !== businessId));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't remove favorite");
    }
  };

  // Lazy-load favorites the first time the tab is opened.
  useEffect(() => {
    if (activeTab === "favorites" && user) {
      fetchFavorites();
    }
  }, [activeTab, user]);

  const handleBusinessDelete = async () => {
    if (!business) return;
    const confirmed = window.confirm(
      `Delete "${business.name}"? This permanently removes the listing, all its products, reviews, and cancels any active subscription. This cannot be undone.`
    );
    if (!confirmed) return;
    try {
      await axios.delete(
        `${API}/businesses/${business.business_id}`,
        { withCredentials: true }
      );
      toast.success("Business deleted.");
      setBusinessDialogOpen(false);
      // Refresh so we drop this business from local state
      setBusiness(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete business");
    }
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    
    const productData = {
      ...productForm,
      price: parseFloat(productForm.price),
      inventory: parseInt(productForm.inventory) || 0
    };

    try {
      if (editingProduct) {
        await axios.put(
          `${API}/products/${editingProduct.product_id}`,
          productData,
          { withCredentials: true }
        );
        toast.success("Product updated!");
      } else {
        await axios.post(
          `${API}/businesses/${business.business_id}/products`,
          productData,
          { withCredentials: true }
        );
        toast.success("Product created!");
      }
      setProductDialogOpen(false);
      setEditingProduct(null);
      setProductForm({
        name: "",
        description: "",
        price: "",
        category: "",
        image_url: "",
        gallery_images: [],
        inventory: 0,
        modifier_groups: [],
        add_ons: [],
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save product");
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    
    try {
      await axios.delete(`${API}/products/${productId}`, { withCredentials: true });
      toast.success("Product deleted!");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  // Reusable favorites list, used on both the tabbed dashboard and the
  // "no business yet" screen so buyer-only accounts still see their saves.
  const [inlineFavLoaded, setInlineFavLoaded] = useState(false);
  useEffect(() => {
    if (user && !business && !inlineFavLoaded) {
      fetchFavorites();
      setInlineFavLoaded(true);
    }
  }, [user, business, inlineFavLoaded]);

  const renderFavoritesGrid = () => {
    if (favoritesLoading) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-gray-100 rounded-xl h-56 animate-pulse"></div>
          ))}
        </div>
      );
    }
    if (favorites.length === 0) {
      return (
        <Card>
          <CardContent className="py-16 text-center">
            <Heart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl text-tbc-navy mb-2">No favorites yet</h3>
            <p className="text-gray-600 font-body mb-6">
              Tap the heart icon on any business to save it here for quick access.
            </p>
            <Link to="/directory">
              <Button
                className="bg-tbc-navy hover:bg-tbc-navy/90 text-white rounded-full"
                data-testid="browse-directory-btn"
              >
                Browse the Directory
              </Button>
            </Link>
          </CardContent>
        </Card>
      );
    }
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {favorites.map((b) => (
          <Card key={b.business_id} data-testid={`favorite-card-${b.business_id}`}>
            <CardContent className="p-4">
              <Link to={`/business/${b.business_id}`} className="flex items-start gap-3">
                <BusinessLogo name={b.name} logoUrl={b.logo_url} size="lg" shape="rounded" />
                <div className="min-w-0 flex-1">
                  <h3 className="font-heading text-lg text-tbc-navy truncate">{b.name}</h3>
                  <p className="text-xs text-gray-500 font-body mt-0.5">
                    {b.category === "Other" && b.custom_category ? b.custom_category : b.category}
                  </p>
                  {b.city && b.state && (
                    <p className="flex items-center text-xs text-gray-500 font-body mt-1">
                      <MapPin className="w-3 h-3 mr-1" />
                      {b.city}, {b.state}
                    </p>
                  )}
                  {b.is_verified && (
                    <span className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 bg-tbc-gold/20 text-tbc-navy text-[10px] font-semibold rounded-full">
                      <BadgeCheck className="w-3 h-3" />
                      Verified
                    </span>
                  )}
                </div>
              </Link>
              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  onClick={() => removeFavorite(b.business_id)}
                  className="text-xs text-red-600 hover:text-red-700 font-body underline decoration-dotted underline-offset-2"
                  data-testid={`favorite-remove-${b.business_id}`}
                >
                  Remove
                </button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  const openEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      category: product.category || "",
      image_url: product.image_url || "",
      gallery_images: Array.isArray(product.gallery_images) ? product.gallery_images : [],
      inventory: product.inventory || 0,
      modifier_groups: Array.isArray(product.modifier_groups) ? product.modifier_groups : [],
      add_ons: Array.isArray(product.add_ons) ? product.add_ons : [],
    });
    setProductDialogOpen(true);
  };

  // Calculate stats
  const totalRevenue = orders.reduce((sum, order) => 
    order.status === "paid" ? sum + order.total : sum, 0
  );
  const totalOrders = orders.filter(o => o.status === "paid").length;

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="animate-pulse space-y-6">
            <div className="h-40 bg-gray-200 rounded-xl"></div>
            <div className="h-60 bg-gray-200 rounded-xl"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  // If no business, show registration
  if (!business) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 py-16">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-tbc-gold/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Store className="w-10 h-10 text-tbc-gold" />
            </div>
            <h1 className="font-heading text-3xl text-tbc-navy mb-4">Register Your Business</h1>
            <p className="text-gray-600 font-body max-w-md mx-auto">
              Join The Blk Community and connect with customers who want to support Black-owned businesses
            </p>
          </div>

          {/* Card-scan shortcut — snap a photo of a business card and let AI
              pre-fill the form. Users can still edit every field afterward. */}
          <button
            type="button"
            onClick={() => setScanOpen(true)}
            className="w-full mb-4 rounded-xl border-2 border-dashed border-tbc-gold bg-tbc-cream/40 hover:bg-tbc-cream transition-colors px-4 py-4 flex items-center justify-between gap-3 text-left"
            data-testid="scan-business-card-btn"
          >
            <span className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-full bg-tbc-gold/25 flex items-center justify-center flex-none">
                <Sparkles className="w-5 h-5 text-tbc-navy" />
              </span>
              <span>
                <span className="block font-heading text-tbc-navy">Scan a business card</span>
                <span className="text-xs text-gray-600">
                  Snap a photo — we&apos;ll auto-fill the form. Review, edit, then submit.
                </span>
              </span>
            </span>
            <span className="text-tbc-navy text-xs font-semibold underline decoration-dotted">Start</span>
          </button>

          <BusinessCardScanDialog
            open={scanOpen}
            onOpenChange={setScanOpen}
            onScanned={applyScannedCard}
          />

          <form onSubmit={handleBusinessSubmit} className="bg-white rounded-xl p-6 shadow-sm space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Business Name *</Label>
                <Input
                  value={businessForm.name}
                  onChange={(e) => setBusinessForm({ ...businessForm, name: e.target.value })}
                  required
                  placeholder="Your business name"
                  data-testid="business-name-input"
                />
              </div>
              
              <div>
                <Label>Category *</Label>
                <Select
                  value={businessForm.category}
                  onValueChange={(value) => setBusinessForm({ ...businessForm, category: value })}
                >
                  <SelectTrigger data-testid="business-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {businessForm.category === "Other" && (
                <div>
                  <Label>Specify your field *</Label>
                  <Input
                    value={businessForm.custom_category}
                    onChange={(e) => setBusinessForm({ ...businessForm, custom_category: e.target.value })}
                    placeholder="e.g. DJ services, tax prep, event catering"
                    required={businessForm.category === "Other"}
                    data-testid="custom-category-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This shows up on your business profile in place of &ldquo;Other&rdquo;.
                  </p>
                </div>
              )}

              <div className="md:col-span-2">
                <Label>Description *</Label>
                <Textarea
                  value={businessForm.description}
                  onChange={(e) => setBusinessForm({ ...businessForm, description: e.target.value })}
                  required
                  rows={6}
                  placeholder="Tell customers about your business..."
                  data-testid="business-description-input"
                  className="min-h-[160px] leading-relaxed"
                />
                <p className="text-xs text-gray-500 mt-1 font-body">
                  {(businessForm.description || "").length} characters — press Enter for new paragraphs.
                </p>
              </div>

              <div>
                <Label>City</Label>
                <Input
                  value={businessForm.city || ""}
                  onChange={(e) => setBusinessForm({ ...businessForm, city: e.target.value })}
                  placeholder="e.g., Minneapolis"
                />
              </div>
              <div>
                <Label>State</Label>
                <StateSelect
                  value={businessForm.state}
                  onChange={(code) => setBusinessForm({ ...businessForm, state: code })}
                  testId="signup-state-select"
                />
              </div>
              <div>
                <Label>ZIP Code</Label>
                <Input
                  value={businessForm.zip_code || ""}
                  onChange={(e) => setBusinessForm({ ...businessForm, zip_code: e.target.value })}
                  placeholder="e.g., 55401"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={businessForm.phone || ""}
                  onChange={(e) => setBusinessForm({ ...businessForm, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={businessForm.email || ""}
                  onChange={(e) => setBusinessForm({ ...businessForm, email: e.target.value })}
                  placeholder="contact@yourbusiness.com"
                />
              </div>
              <div>
                <Label>Website</Label>
                <Input
                  value={businessForm.website || ""}
                  onChange={(e) => setBusinessForm({ ...businessForm, website: e.target.value })}
                  placeholder="https://yourbusiness.com"
                />
              </div>
              <div className="md:col-span-2">
                <SocialLinksInput
                  value={businessForm.social_links || {}}
                  onChange={(next) => setBusinessForm({ ...businessForm, social_links: next })}
                />
              </div>
              <div className="md:col-span-2">
                <Label>Business Image</Label>
                <ImageUpload
                  currentImageUrl={businessForm.image_url}
                  imageType="business"
                  onUploadSuccess={(url) => setBusinessForm({ ...businessForm, image_url: url || "" })}
                />
              </div>
            </div>
            
            <Button
              type="submit"
              className="w-full bg-tbc-navy hover:bg-tbc-navy/90 text-white py-6 rounded-full text-lg"
              data-testid="create-business-button"
            >
              Create Business
            </Button>
          </form>

          {/* My Favorites — buyers who don't own a business still land here,
              so surface their saved directory bookmarks. */}
          <div className="mt-10">
            <h2 className="font-heading text-2xl text-tbc-navy mb-4 flex items-center gap-2">
              <Heart className="w-5 h-5 text-red-500" />
              My Favorites ({favorites.length})
            </h2>
            {renderFavoritesGrid()}
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-7xl mx-auto flex gap-6 lg:px-4">
        {/* Desktop-only left sidebar (owner). Drives the same activeTab state
            the horizontal tab bar uses on tablet/mobile — no parallel routing. */}
        <OwnerSidebarNav
          activeTab={activeTab}
          focus={null}
          businessName={business?.name}
          onSelect={(tab) => {
            setActiveTab(tab);
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              next.set("tab", tab);
              return next;
            });
          }}
        />
        <div className="flex-1 min-w-0 px-4 sm:px-6 lg:px-4 py-8">
        {/* Super-Admin impersonation banner — visible ONLY when the dashboard
            was opened via ?admin_business_id=biz_xxx and the target business
            is not owned by the current user. Keeps the admin honest and
            offers a one-tap escape back to Business Management. */}
        {business?._admin_impersonated && (
          <div
            className="mb-6 rounded-2xl border-2 border-tbc-gold bg-tbc-gold/10 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
            data-testid="dashboard-super-admin-banner"
          >
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-tbc-gold/25 flex items-center justify-center flex-none">
                <span className="text-tbc-navy text-lg font-heading">S</span>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-tbc-gold font-semibold">Super Admin Mode</p>
                <p className="text-sm text-tbc-navy mt-0.5">
                  Editing <strong>{business.name}</strong>
                  {business.owner?.email ? <> · Owner: <span className="font-mono text-xs">{business.owner.email}</span></> : null}
                </p>
                <p className="text-[11px] text-tbc-navy/70 mt-1">Every change is written to the admin audit log.</p>
              </div>
            </div>
            <Link
              to="/admin?tab=biz-mgmt"
              className="rounded-full bg-tbc-navy text-white text-sm px-4 py-2 hover:bg-tbc-navy/90 whitespace-nowrap inline-flex items-center gap-1.5"
              data-testid="dashboard-back-to-biz-mgmt"
            >
              ← Back to Business Search
            </Link>
          </div>
        )}

        {/* Admin quick search — visible only to admins so they can jump to
            any business from their landing dashboard without going to /admin */}
        {user?.is_admin && !business?._admin_impersonated && (
          <div
            className="mb-6 bg-tbc-navy rounded-2xl p-4 shadow-sm"
            data-testid="dashboard-admin-search"
          >
            <p className="text-[10px] uppercase tracking-widest text-tbc-gold font-semibold mb-2">
              Admin quick search
            </p>
            <AdminHeaderBusinessSearch />
          </div>
        )}

        {/* Multi-business switcher — only shown when the owner has more than one
            listing OR the current user is a super admin (in which case we
            show the platform total + a Search CTA even if they own zero) */}
        {(businesses.length > 1 || user?.is_admin) && (
          <div
            className="mb-6 bg-white border border-tbc-navy/15 rounded-2xl p-4 shadow-sm"
            data-testid="business-switcher"
          >
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div>
                {user?.is_admin ? (
                  <>
                    <p className="font-heading text-tbc-navy text-base">
                      {adminStats?.businesses ? (
                        <>Platform total: <span className="text-tbc-gold">{adminStats.businesses.toLocaleString()}</span> businesses</>
                      ) : (
                        <>Super Admin · <span className="text-tbc-gold">{businesses.length}</span> business{businesses.length === 1 ? "" : "es"} in your workspace</>
                      )}
                    </p>
                    <p className="text-xs text-gray-500 font-body">
                      {adminStats?.businesses
                        ? `You own ${businesses.length} · use search above to manage any of the other ${(adminStats.businesses - businesses.length).toLocaleString()}.`
                        : "Use the switcher below or the header search to open any business on TheBlkCommunity."}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-heading text-tbc-navy text-base">
                      You manage <span className="text-tbc-gold">{businesses.length}</span> business{businesses.length === 1 ? "" : "es"}
                    </p>
                    <p className="text-xs text-gray-500 font-body">
                      Click a business to manage its products, orders, and profile.
                    </p>
                  </>
                )}
              </div>
              <div className="text-xs text-gray-500 font-body">
                Currently editing: <span className="font-medium text-tbc-navy">{business.name}</span>
              </div>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
              {businesses.map((b) => {
                const active = b.business_id === business.business_id;
                return (
                  <button
                    key={b.business_id}
                    onClick={() => switchBusiness(b)}
                    className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors border ${
                      active
                        ? "bg-tbc-navy text-white border-tbc-navy"
                        : "bg-white text-tbc-navy border-gray-200 hover:border-tbc-gold hover:text-tbc-gold"
                    }`}
                    data-testid={`business-switcher-${b.business_id}`}
                  >
                    {b.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Header */}
        <div
          className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8"
          data-testid="dashboard-business-header"
        >
          <div className="flex items-center gap-4">
            <div
              className="w-16 h-16 bg-tbc-cream rounded-xl overflow-hidden flex-shrink-0"
              data-testid="dashboard-logo-section"
            >
              {business.image_url ? (
                <img src={resolveAssetUrl(business.image_url)} alt={business.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Store className="w-8 h-8 text-tbc-gold" />
                </div>
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-heading text-2xl text-tbc-navy">{business.name}</h1>
                {business.is_verified && (
                  <BadgeCheck className="w-5 h-5 text-tbc-gold" />
                )}
              </div>
              <p className="text-gray-500 font-body text-sm">
                {business.category === "Other" && business.custom_category 
                  ? business.custom_category 
                  : business.category}
                {business.city && ` · ${business.city}, ${business.state}`}
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <Link to="/business-academy">
              <Button
                variant="outline"
                className="border-tbc-gold/60 text-tbc-navy hover:bg-tbc-gold/10"
                data-testid="dashboard-academy-btn"
              >
                <GraduationCap className="w-4 h-4 mr-2 text-tbc-gold" />
                Business Academy
              </Button>
            </Link>
            <Link to={`/business/${business.business_id}`}>
              <Button variant="outline" className="border-gray-200" data-testid="view-storefront">
                <Eye className="w-4 h-4 mr-2" />
                View Storefront
              </Button>
            </Link>
            <Dialog open={businessDialogOpen} onOpenChange={setBusinessDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-gray-200" data-testid="edit-business-button">
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl">Edit Business</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleBusinessSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label>Business Name *</Label>
                      <Input
                        value={businessForm.name}
                        onChange={(e) => setBusinessForm({ ...businessForm, name: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label>Category *</Label>
                      <Select
                        value={businessForm.category}
                        onValueChange={(value) => setBusinessForm({ ...businessForm, category: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((cat) => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {businessForm.category === "Other" && (
                      <div>
                        <Label>Specify your field *</Label>
                        <Input
                          value={businessForm.custom_category}
                          onChange={(e) => setBusinessForm({ ...businessForm, custom_category: e.target.value })}
                          placeholder="e.g. DJ services, tax prep, event catering"
                          required
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          This shows up on your business profile in place of &ldquo;Other&rdquo;.
                        </p>
                      </div>
                    )}
                    <div className="col-span-2">
                      <Label>Description *</Label>
                      <Textarea
                        value={businessForm.description}
                        onChange={(e) => setBusinessForm({ ...businessForm, description: e.target.value })}
                        required
                        rows={6}
                        className="min-h-[160px] leading-relaxed"
                      />
                      <p className="text-xs text-gray-500 mt-1 font-body">
                        {(businessForm.description || "").length} characters — press Enter for new paragraphs.
                      </p>
                    </div>
                    <div>
                      <Label>City</Label>
                      <Input
                        value={businessForm.city || ""}
                        onChange={(e) => setBusinessForm({ ...businessForm, city: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>State</Label>
                      <StateSelect
                        value={businessForm.state}
                        onChange={(code) => setBusinessForm({ ...businessForm, state: code })}
                        testId="edit-state-select"
                      />
                    </div>
                    <div>
                      <Label>ZIP Code</Label>
                      <Input
                        value={businessForm.zip_code || ""}
                        onChange={(e) => setBusinessForm({ ...businessForm, zip_code: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={businessForm.phone || ""}
                        onChange={(e) => setBusinessForm({ ...businessForm, phone: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={businessForm.email || ""}
                        onChange={(e) => setBusinessForm({ ...businessForm, email: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Website</Label>
                      <Input
                        value={businessForm.website || ""}
                        onChange={(e) => setBusinessForm({ ...businessForm, website: e.target.value })}
                      />
                    </div>
                    <div className="col-span-2">
                      <SocialLinksInput
                        value={businessForm.social_links || {}}
                        onChange={(next) => setBusinessForm({ ...businessForm, social_links: next })}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Business Image</Label>
                      <ImageUpload
                        currentImageUrl={businessForm.image_url}
                        imageType="business"
                        onUploadSuccess={(url) => setBusinessForm({ ...businessForm, image_url: url || "" })}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 pt-2">
                    <Button
                      type="submit"
                      className="flex-1 bg-tbc-navy hover:bg-tbc-navy/90 text-white"
                      data-testid="save-business-button"
                    >
                      Save Changes
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setBusinessDialogOpen(false)}
                      className="sm:w-32"
                      data-testid="cancel-business-button"
                    >
                      Cancel
                    </Button>
                    {business && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleBusinessDelete}
                        className="sm:w-32 border-red-300 text-red-600 hover:bg-red-50"
                        data-testid="delete-business-button"
                      >
                        <Trash2 className="w-4 h-4 mr-1.5" /> Delete
                      </Button>
                    )}
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex gap-1 mb-6 bg-white rounded-lg p-1 w-fit">
          {[
            { id: "overview", label: "Overview", icon: BarChart3 },
            { id: "products", label: "Products", icon: Package },
            { id: "orders", label: "Orders", icon: ShoppingBag },
            { id: "favorites", label: "Favorites", icon: Heart }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md font-body text-sm transition-colors ${
                activeTab === tab.id
                  ? "bg-tbc-navy text-white"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="space-y-6">
            {/* Redesigned Owner overview hero — welcome, business card,
                action-needed, quick actions, stats strip, tools, upgrade
                CTA. Uses existing data on the page (no fresh fetches). */}
            <div className="flex gap-6">
              <div className="flex-1 min-w-0 space-y-6">
                <OwnerDashboardOverview
                  business={business}
                  user={user}
                  productCount={products?.length || 0}
                  totalOrders={totalOrders}
                  totalRevenue={totalRevenue}
                  onSelectTab={(tab) => {
                    setActiveTab(tab);
                    setSearchParams((prev) => {
                      const next = new URLSearchParams(prev);
                      next.set("tab", tab);
                      return next;
                    });
                  }}
                />
              </div>
              {/* Desktop-only right rail: Recent Activity, Orders summary,
                  Refer a Business, "Together We Build More" banner. */}
              <OwnerDashboardRightRail
                business={business}
                onSelectTab={(tab) => {
                  setActiveTab(tab);
                  setSearchParams((prev) => {
                    const next = new URLSearchParams(prev);
                    next.set("tab", tab);
                    return next;
                  });
                }}
              />
            </div>

            {/* Business Academy — computed progress checklist */}
            <GetStartedChecklist
              business={business}
              onStartTour={() => setTourRunning(true)}
            />

            {/* Category-specific owner editor — renders the tagline /
                specialties / hero portrait form only when the selected
                business is a realtor. Other categories can plug in their
                own editor here in the future. */}
            <RealtorOwnerEditor
              business={business}
              onSaved={(updated) => {
                if (!updated) return;
                setBusiness(updated);
                setBusinesses((list) =>
                  list.map((b) =>
                    b.business_id === updated.business_id ? updated : b,
                  ),
                );
              }}
            />

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 font-body">Total Revenue</p>
                      <p className="text-2xl font-heading text-tbc-navy">${totalRevenue.toFixed(2)}</p>
                    </div>
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 font-body">Total Orders</p>
                      <p className="text-2xl font-heading text-tbc-navy">{totalOrders}</p>
                    </div>
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <ShoppingBag className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 font-body">Products</p>
                      <p className="text-2xl font-heading text-tbc-navy">{products.length}</p>
                    </div>
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                      <Package className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 font-body">BC Score</p>
                      <p className="text-2xl font-heading text-tbc-navy">
                        {business.bc_score || "—"}/5
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                      <Star className="w-6 h-6 text-yellow-600" />
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    {business.bc_score_count || 0} reviews
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Recent Orders */}
            {orders.length > 0 && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="font-heading text-lg">Recent Orders</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setActiveTab("orders")}>
                    View All
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {orders.slice(0, 5).map((order) => (
                      <div
                        key={order.order_id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-tbc-navy">
                            Order #{order.order_id.slice(-8).toUpperCase()}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(order.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-heading text-tbc-navy">${order.total.toFixed(2)}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            order.status === "paid" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"
                          }`}>
                            {order.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Products Tab */}
        {activeTab === "products" && (
          <div className="space-y-6">
            <div className="flex justify-between items-center flex-wrap gap-2">
              <h2 className="font-heading text-xl text-tbc-navy flex items-center gap-1">
                Your Products ({products.length})
                <ContextualHelp
                  title="Adding Products"
                  body="Products appear in Shop Black on Blinterest and on your storefront. Use clear photos, honest descriptions and competitive pricing."
                  tutorialSlug="add-products"
                  testid="help-products"
                />
              </h2>
              <div className="flex items-center gap-2">
                <BulkProductUpload
                  businessId={business.business_id}
                  businessName={business.name}
                  buttonClassName="border-tbc-navy text-tbc-navy hover:bg-tbc-navy/5"
                  onCreated={() => fetchData()}
                />
              <Dialog open={productDialogOpen} onOpenChange={(open) => {
                setProductDialogOpen(open);
                if (!open) {
                  setEditingProduct(null);
                  setProductForm({
                    name: "",
                    description: "",
                    price: "",
                    category: "",
                    image_url: "",
                    inventory: 0
                  });
                }
              }}>
                <DialogTrigger asChild>
                  <Button className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy" data-testid="add-product-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Product
                  </Button>
                </DialogTrigger>
                <DialogContent
                  className={
                    // On mobile fill the whole visible viewport (100dvh handles
                    // iOS Safari's dynamic bottom bar) so the sticky footer
                    // stays reachable no matter how tall the form grows —
                    // uploading photos, adding modifier groups or paid
                    // add-ons all keep the Update button visible. On sm+ the
                    // dialog reverts to the shadcn-default centered card.
                    "max-w-lg p-0 gap-0 flex flex-col " +
                    "top-0 left-0 translate-x-0 translate-y-0 h-[100dvh] max-h-[100dvh] w-full rounded-none " +
                    "sm:top-[50%] sm:left-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:h-auto sm:max-h-[90vh] sm:w-full sm:rounded-lg"
                  }
                >
                  <DialogHeader className="px-6 pt-6 pb-3 border-b border-gray-100 flex-shrink-0">
                    <DialogTitle className="font-heading text-2xl">
                      {editingProduct ? "Edit Product" : "Add Product"}
                    </DialogTitle>
                  </DialogHeader>
                  <form
                    onSubmit={handleProductSubmit}
                    className="flex-1 min-h-0 flex flex-col"
                  >
                    <div
                      className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4 [-webkit-overflow-scrolling:touch]"
                      data-testid="product-form-scroll"
                    >
                    <div>
                      <Label>Product Name *</Label>
                      <Input
                        value={productForm.name}
                        onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                        required
                        data-testid="product-name-input"
                      />
                    </div>
                    <div>
                      <Label>Description *</Label>
                      <Textarea
                        value={productForm.description}
                        onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                        required
                        rows={3}
                        data-testid="product-description-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Price ($) *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={productForm.price}
                          onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                          required
                          data-testid="product-price-input"
                        />
                      </div>
                      <div>
                        <Label>Inventory</Label>
                        <Input
                          type="number"
                          min="0"
                          value={productForm.inventory}
                          onChange={(e) => setProductForm({ ...productForm, inventory: parseInt(e.target.value) || 0 })}
                          data-testid="product-inventory-input"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Category</Label>
                      <Input
                        value={productForm.category}
                        onChange={(e) => setProductForm({ ...productForm, category: e.target.value })}
                        placeholder="e.g., T-Shirts, Jewelry"
                      />
                    </div>
                    <div>
                      <Label>Product Photos</Label>
                      {(() => {
                        const isPremium = business?.subscription_tier &&
                          ["premium", "founding"].includes(business.subscription_tier);
                        const maxImages = isPremium ? 8 : 1;
                        return (
                          <>
                            <p className="text-[11px] text-tbc-navy/50 mt-0.5 mb-2">
                              {isPremium
                                ? "Upload up to 8 shots. Tile 1 is the cover shown on cards, cart, and the storefront lightbox."
                                : "Free plan: 1 photo per product. Upgrade to Premium to add up to 8 shots for a proper storefront gallery."}
                            </p>
                            <MultiImageUpload
                              value={
                                productForm.gallery_images && productForm.gallery_images.length
                                  ? productForm.gallery_images
                                  : (productForm.image_url ? [productForm.image_url] : [])
                              }
                              onChange={(urls) => setProductForm({
                                ...productForm,
                                gallery_images: urls,
                                // Keep image_url in sync with tile 1 so legacy readers
                                // (cart, storefront cards, admin) still see the cover.
                                image_url: urls[0] || "",
                              })}
                              maxImages={maxImages}
                              imageType="product"
                            />
                          </>
                        );
                      })()}
                    </div>
                    {/* Modifier groups + paid add-ons — powers the storefront
                        product-detail options UI and cart line breakdowns. */}
                    <div>
                      <Label>Options & extras</Label>
                      <p className="text-[11px] text-tbc-navy/50 mt-0.5 mb-2">
                        Configure required option groups (like "Choose 2 sides") and paid add-ons.
                      </p>
                      <ProductModifierEditor
                        modifierGroups={productForm.modifier_groups || []}
                        addOns={productForm.add_ons || []}
                        onChange={({ modifier_groups, add_ons }) =>
                          setProductForm({ ...productForm, modifier_groups, add_ons })
                        }
                      />
                    </div>
                    </div>
                    {/* Sticky action bar — reachable from any form height, and
                        offset for iOS Safari's home-indicator safe area. */}
                    <div
                      className="flex-shrink-0 border-t border-gray-100 bg-white px-6 py-3 pb-[calc(12px+env(safe-area-inset-bottom))]"
                      data-testid="product-form-actions"
                    >
                      <Button
                        type="submit"
                        className="w-full bg-tbc-navy hover:bg-tbc-navy/90 text-white"
                        data-testid="save-product-button"
                      >
                        {editingProduct ? "Update Product" : "Add Product"}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
              </div>
            </div>

            {products.length === 0 ? (
              <Card>
                <CardContent className="py-16 text-center">
                  <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="font-heading text-xl text-tbc-navy mb-2">No products yet</h3>
                  <p className="text-gray-600 font-body mb-6">Add your first product to start selling</p>
                  <Button
                    onClick={() => setProductDialogOpen(true)}
                    className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Your First Product
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {products.map((product) => (
                  <Card key={product.product_id} className="overflow-hidden" data-testid={`dashboard-product-${product.product_id}`}>
                    <div className="aspect-square bg-gray-100 overflow-hidden">
                      <img
                        src={resolveAssetUrl(product.image_url) || "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&q=80"}
                        alt={product.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          // Legacy products may have been saved with a
                          // broken doubled-`/api/api/` prefix (fixed in
                          // ImageUpload.jsx). Show the shared TBC food
                          // placeholder instead of the browser's jagged icon.
                          if (e.currentTarget.dataset.fallback !== "1") {
                            e.currentTarget.dataset.fallback = "1";
                            e.currentTarget.src = "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&q=80";
                          }
                        }}
                      />
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-heading text-lg text-tbc-navy line-clamp-1">{product.name}</h3>
                      <p className="text-gray-600 text-sm font-body line-clamp-2 mt-1 h-10">
                        {product.description}
                      </p>
                      <div className="flex items-center justify-between mt-4">
                        <span className="font-heading text-xl text-tbc-gold">
                          ${product.price.toFixed(2)}
                        </span>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditProduct(product)}
                            className="h-8 w-8"
                            data-testid={`edit-product-${product.product_id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteProduct(product.product_id)}
                            className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                            data-testid={`delete-product-${product.product_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <p className={`text-xs mt-2 font-body ${product.inventory > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {product.inventory > 0 ? `${product.inventory} in stock` : 'Out of stock'}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === "orders" && (
          <div className="space-y-6">
            <h2 className="font-heading text-xl text-tbc-navy">Orders ({orders.length})</h2>
            
            {orders.length === 0 ? (
              <Card>
                <CardContent className="py-16 text-center">
                  <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="font-heading text-xl text-tbc-navy mb-2">No orders yet</h3>
                  <p className="text-gray-600 font-body">Orders will appear here when customers purchase your products</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => (
                  <Card key={order.order_id} data-testid={`order-${order.order_id}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <p className="font-heading text-lg text-tbc-navy">
                            Order #{order.order_id.slice(-8).toUpperCase()}
                          </p>
                          <p className="text-sm text-gray-500 font-body">
                            {new Date(order.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium capitalize ${
                          order.status === "paid" ? "bg-green-100 text-green-700" :
                          order.status === "shipped" ? "bg-blue-100 text-blue-700" :
                          order.status === "delivered" ? "bg-purple-100 text-purple-700" :
                          "bg-yellow-100 text-yellow-700"
                        }`}>
                          {order.status}
                        </span>
                      </div>
                      
                      <div className="space-y-2 border-t pt-4">
                        {order.items.map((item, idx) => (
                          <div key={`${order.order_id}-item-${item.product_id || idx}`} className="flex justify-between text-sm font-body">
                            <span className="text-gray-600">
                              {item.product_name} × {item.quantity}
                            </span>
                            <span className="text-tbc-navy">${item.subtotal.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                      
                      <div className="border-t mt-4 pt-4 flex justify-between">
                        <span className="font-medium text-gray-600">Total</span>
                        <span className="font-heading text-xl text-tbc-navy">${order.total.toFixed(2)}</span>
                      </div>

                      {/* Seller status actions */}
                      {(order.status === "paid" || order.status === "shipped") && (
                        <div className="border-t mt-4 pt-4 flex flex-wrap gap-2 justify-end">
                          {order.status === "paid" && (
                            <Button
                              size="sm"
                              onClick={() => updateOrderStatus(order.order_id, "shipped")}
                              className="bg-tbc-navy hover:bg-tbc-navy/90 text-white"
                              data-testid={`order-mark-shipped-${order.order_id}`}
                            >
                              Mark as Shipped
                            </Button>
                          )}
                          {order.status === "shipped" && (
                            <Button
                              size="sm"
                              onClick={() => updateOrderStatus(order.order_id, "delivered")}
                              className="bg-tbc-navy hover:bg-tbc-navy/90 text-white"
                              data-testid={`order-mark-delivered-${order.order_id}`}
                            >
                              Mark as Delivered
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateOrderStatus(order.order_id, "cancelled")}
                            className="border-red-200 text-red-600 hover:bg-red-50"
                            data-testid={`order-cancel-${order.order_id}`}
                          >
                            Cancel Order
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Favorites Tab — businesses this user has hearted anywhere on the site. */}
        {activeTab === "favorites" && (
          <div className="space-y-6">
            <h2 className="font-heading text-xl text-tbc-navy" data-testid="favorites-heading">
              My Favorites ({favorites.length})
            </h2>
            {renderFavoritesGrid()}
          </div>
        )}

        {/* Payouts Tab — Stripe Connect onboarding + status. */}
        {activeTab === "payouts" && (
          <div className="space-y-6" data-testid="payouts-tab">
            <StripeConnectCard business={business} />
          </div>
        )}

        {/* Featured Placement Tab — premium category spotlight slot. */}
        {activeTab === "featured" && (
          <div className="space-y-6" data-testid="featured-tab">
            <FeatureSlotCard />
            <BannerAdsCard business={business} categories={categories} />
          </div>
        )}

        {/* Analytics Tab — profile / product views + top products (premium). */}
        {activeTab === "analytics" && (
          <div className="space-y-6" data-testid="analytics-tab">
            <AnalyticsCard business={business} />
            <BlinterestAnalyticsCard business={business} />
          </div>
        )}

        {/* Profile Tab — edit the business record via the existing dialog. */}
        {activeTab === "profile" && (
          <div className="space-y-6" data-testid="profile-tab">
            <OwnerEmptyState
              icon={Store}
              title="Finish your business profile"
              subtitle="A complete profile builds trust with buyers. Add your description, hours, and contact info."
              ctaLabel="Complete Profile"
              onCta={() => setBusinessDialogOpen(true)}
              testid="profile-empty"
            />
            {business && (
              <FulfillmentSettingsCard business={business} onSaved={fetchData} />
            )}
          </div>
        )}

        {/* Customers / Leads Tab — no CRM yet, show designed empty state. */}
        {activeTab === "customers" && (
          <div className="space-y-6" data-testid="customers-tab">
            <OwnerEmptyState
              icon={Users}
              title="No customers yet"
              subtitle="When buyers purchase from or contact your business, they'll appear here so you can follow up and build relationships."
              testid="customers-empty"
            />
          </div>
        )}

        {/* Reviews Tab — read-only owner view of the review section. */}
        {activeTab === "reviews" && (
          <div className="space-y-6" data-testid="reviews-tab">
            {business?.bc_score_count ? (
              <div className="rounded-2xl border border-tbc-navy/10 bg-white p-6">
                <p className="text-[10px] uppercase tracking-[0.28em] text-tbc-navy/60 font-semibold mb-2">Your Reviews</p>
                <div className="flex items-baseline gap-3">
                  <p className="font-heading text-4xl text-tbc-navy">{(business.bc_score || 0).toFixed(1)}</p>
                  <p className="text-sm text-tbc-navy/60">/ 5 · {business.bc_score_count} review{business.bc_score_count === 1 ? "" : "s"}</p>
                </div>
                <Link
                  to={`/business/${business.business_id}`}
                  className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-tbc-gold hover:text-tbc-gold/80"
                >
                  Read reviews on your profile →
                </Link>
              </div>
            ) : (
              <OwnerEmptyState
                icon={MessageSquare}
                title="No reviews yet"
                subtitle="When buyers rate your business, their reviews land here. Share your storefront to invite the first one."
                ctaLabel="View Storefront"
                ctaHref={business ? `/business/${business.business_id}` : "/"}
                testid="reviews-empty"
              />
            )}
          </div>
        )}

        {/* Referrals Tab — full-width referral card. */}
        {activeTab === "referrals" && (
          <div className="space-y-6" data-testid="referrals-tab">
            <ReferralCard />
          </div>
        )}

        {/* Settings Tab — subscription + weekly digest for now. */}
        {activeTab === "settings" && (
          <div className="space-y-6" data-testid="settings-tab">
            <SubscriptionCard business={business} />
            <DigestOptInToggle />
          </div>
        )}

        {/* QR Code Tab — routes to the dedicated /cards page. */}
        {activeTab === "qr" && (
          <div className="space-y-6" data-testid="qr-tab">
            <OwnerEmptyState
              icon={QrCode}
              title="Your Business QR Cards"
              subtitle="Grab printable QR cards that link straight to your TBC storefront — perfect for markets, pop-ups, and business cards."
              ctaLabel="Open QR Cards"
              ctaHref="/cards"
              testid="qr-empty"
            />
          </div>
        )}
        </div>
      </div>

      {/* Academy — first-time welcome + guided tour. Both mount once the
          business object is loaded; the welcome modal auto-closes for
          returning owners and the tour only runs when `tourRunning`. */}
      {business && (
        <>
          <FirstTimeWelcome
            business={business}
            onStartTour={() => setTourRunning(true)}
          />
          <DashboardTour
            business={business}
            running={tourRunning}
            onFinish={() => setTourRunning(false)}
          />
        </>
      )}

      <Footer />
    </div>
  );
}
